/* eslint-disable camelcase */
import { convertToCents } from '../helpers/format-money';
import { createElement, error } from 'client-portal/utils/banners';

const appearance = {
  variables: {
    borderRadius: '2px',
    colorDanger: '#ea3917',
    colorText: '#212121',
    fontFamily: 'Inter, Helvetica, Arial, sans-serif',
    fontLineHeight: '1.2em',
    fontSizeBase: '16px',
    gridColumnSpacing: '30px',
    gridRowSpacing: '23px',
  },
  rules: {
    '.Input': {
      padding: '16px 12px',
      border: '1px solid #ddd',
      outline: 'none',
    },
    '.Input:focus': {
      borderColor: '#339967',
      boxShadow: '0 0 0 3px #e6f2ec',
    },
    '.Input:hover': {
      borderColor: '#339967',
    },
    '.Input--invalid, .Input--invalid:hover, .Input--invalid:focus': {
      border: '1px solid #ea3917',
      boxShadow: 'none',
    },
    '.Label': {
      fontWeight: '600',
      marginBottom: '8px',
      color: '#333',
      fontSize: '14px',
    },
    '.Error': {
      marginTop: '8px',
    },
  },
};

export function buildStripeSetupModeElement(stripe, opts = {}) {
  let options = {
    mode: 'setup',
    paymentMethodTypes: ['card'],
    appearance,
  };

  if (opts.featureStripeLinkPassthroughMode) {
    delete options.paymentMethodTypes;

    options.currency = opts.currency?.toLowerCase() || 'usd';
    options.onBehalfOf = opts.onBehalfOf;
  }

  if (opts.locale) {
    options.locale = opts.locale;
  }

  return stripe.elements(options);
}

export function buildStripePaymentModeElement(stripe, opts = {}) {
  let paymentOptions = {
    mode: 'payment',
    currency: opts.currency?.toLowerCase() || 'usd',
    onBehalfOf: opts.onBehalfOf,
    paymentMethodTypes: opts.paymentMethodTypes || ['card'],
    paymentMethodCreation: 'manual',
    amount: convertToCents(opts.amount),
    appearance,
  };

  if (opts.featureStripeLinkPassthroughMode) {
    delete paymentOptions.paymentMethodTypes;
  }

  if (opts.locale) {
    paymentOptions.locale = opts.locale;
  }

  return stripe.elements(paymentOptions);
}

export function buildStripePaymentElement(elements, opts = {}) {
  let wallets = { googlePay: 'never', applePay: 'never' };

  if (!opts.featureStripeLinkPassthroughMode) {
    wallets.link = 'never';
  }

  let options = {
    terms: { card: 'never' },
    wallets,
  };

  if (opts.email || opts.phone) {
    options.defaultValues = {
      billingDetails: {
        ...(opts.email && { email: opts.email }),
        ...(opts.phone && { phone: `${opts.phone}` }),
      },
    };
  }

  let paymentElement = elements.create('payment', options);
  paymentElement.on('ready', opts.onReady || (() => {}));
  return paymentElement;
}

export function buildStripeExpressCheckoutElement(elements, callBackOptions = {}) {
  let expressCheckoutOptions = {
    wallets: {
      googlePay: 'never',
      applePay: 'never',
    },
  };
  let events = [
    ['ready', 'onReady'],
    ['loaderror', 'onLoadError'],
    ['confirm', 'onConfirm'],
    ['click', 'onClick'],
    ['cancel', 'onCancel'],
  ];

  let expressCheckoutElement = elements.create('expressCheckout', expressCheckoutOptions);

  events.forEach(([event, callback]) => {
    expressCheckoutElement.on(event, callBackOptions[callback] || (() => {}));
  });

  return expressCheckoutElement;
}

export async function confirmSetupIntent(
  setupIntent,
  elements,
  stripe,
  options = {},
  errorHandler = _errorBanner
) {
  if (setupIntent.isTest) {
    return _buildTestPaymentMethod(setupIntent.stripeCustomerId);
  } else {
    try {
      let setupIntentConfirmation = await _confirmSetupIntent(
        setupIntent,
        elements,
        stripe,
        options
      );

      return setupIntentConfirmation.setupIntent;
    } catch (err) {
      errorHandler(err.message, options);
    }
  }
}

async function _confirmSetupIntent(setupIntent, elements, stripe, options) {
  let confirmation = await stripe.confirmSetup({
    elements,
    clientSecret: setupIntent.clientSecret,
    confirmParams: {
      return_url: window.location.href,
      expand: ['payment_method'],
      payment_method_data: {
        billing_details: {
          name: options.cardholderName,
        },
      },
    },
    handleActions: false,
  });

  if (confirmation.error) {
    throw new Error(confirmation.error.message);
  }

  if (confirmation.setupIntent.status === 'requires_action') {
    throw new Error(
      'The card was declined because it requires authentication. Please try another card'
    );
  }

  return confirmation;
}

function _buildTestPaymentMethod(stripeCustomerId) {
  return {
    payment_method: {
      id: 'pm_123',
      card: {
        brand: 'visa',
        last4: '4242',
        exp_month: 10,
        exp_year: 2023,
      },
      billing_details: {
        name: 'Test payment method name',
      },
      customer: stripeCustomerId,
    },
  };
}

function _errorBanner(message, options) {
  let title = 'Could not add a card';

  if (options.intl) {
    title = options.intl.t('billing.errors.couldNotAddCard');
  }

  error({
    title,
    isTransient: true,
    content: createElement('p', { innerText: message }),
  });

  if (typeof options.errorTracking === 'function') {
    options.errorTracking(message);
  }
}
